import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { formatDuration } from '../../core/format';
import { ArrowsIn, ArrowsOut, Pause, Play, SpeakerHigh, SpeakerSlash } from '../parts/Icons';

interface MediaState {
  paused: boolean;
  time: number;
  duration: number;
  buffered: { start: number; end: number }[];
  volume: number;
  muted: boolean;
  rate: number;
  ended: boolean;
  waiting: boolean;
}

function read(m: HTMLMediaElement): MediaState {
  const b: { start: number; end: number }[] = [];
  for (let i = 0; i < m.buffered.length; i++)
    b.push({ start: m.buffered.start(i), end: m.buffered.end(i) });
  return {
    paused: m.paused,
    time: m.currentTime,
    duration: Number.isFinite(m.duration) ? m.duration : 0,
    buffered: b,
    volume: m.volume,
    muted: m.muted,
    rate: m.playbackRate,
    ended: m.ended,
    waiting: m.readyState < 3 && !m.paused,
  };
}

export function useMediaState(media: HTMLMediaElement | null): MediaState | null {
  const [st, setSt] = useState<MediaState | null>(null);
  useEffect(() => {
    if (!media) return;
    let raf = 0;
    const sync = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setSt(read(media));
      });
    };
    const events = [
      'play',
      'pause',
      'timeupdate',
      'durationchange',
      'progress',
      'volumechange',
      'ratechange',
      'ended',
      'waiting',
      'playing',
      'seeked',
      'loadedmetadata',
      'canplay',
      'emptied',
    ];
    for (const e of events) media.addEventListener(e, sync);
    sync();
    return () => {
      for (const e of events) media.removeEventListener(e, sync);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [media]);
  return st;
}

const RATES = [0.75, 1, 1.25, 1.5, 2];

function spoken(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const parts: string[] = [];
  if (h) parts.push(`${h} ${h === 1 ? 'hour' : 'hours'}`);
  if (m % 60) parts.push(`${m % 60} ${m % 60 === 1 ? 'minute' : 'minutes'}`);
  parts.push(`${s % 60} ${s % 60 === 1 ? 'second' : 'seconds'}`);
  return parts.join(' ');
}

interface Props {
  media: HTMLMediaElement | null;
  durationHint: number | null;
  /** Fraction (0..1) of the timeline that can be jumped to right now. */
  seekableFraction: number;
  focus: boolean;
  onToggleFocus(): void;
  disabled: boolean;
}

export function Controls({
  media,
  durationHint,
  seekableFraction,
  focus,
  onToggleFocus,
  disabled,
}: Props) {
  const st = useMediaState(media);
  const track = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const duration = st && st.duration > 0 ? st.duration : (durationHint ?? 0);
  const time = drag ?? st?.time ?? 0;
  const frac = duration > 0 ? Math.min(1, time / duration) : 0;

  const timeAt = useCallback(
    (clientX: number) => {
      const r = track.current?.getBoundingClientRect();
      if (!r || duration <= 0) return 0;
      return Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * duration;
    },
    [duration],
  );

  const commit = useCallback(
    (t: number) => {
      if (!media || duration <= 0) return;
      const limit = duration * seekableFraction;
      media.currentTime = Math.max(0, Math.min(t, seekableFraction >= 1 ? duration : limit));
    },
    [media, duration, seekableFraction],
  );

  const onDown = (e: PointerEvent<HTMLDivElement>) => {
    if (disabled || duration <= 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag(timeAt(e.clientX));
  };
  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const t = timeAt(e.clientX);
    setHover(t);
    if (drag !== null) setDrag(t);
  };
  const onUp = (e: PointerEvent<HTMLDivElement>) => {
    if (drag === null) return;
    const t = timeAt(e.clientX);
    setDrag(null);
    commit(t);
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!media || disabled) return;
    const step = e.shiftKey ? 30 : 5;
    const cur = media.currentTime;
    const map: Record<string, number | undefined> = {
      ArrowRight: cur + step,
      ArrowUp: cur + step,
      ArrowLeft: cur - step,
      ArrowDown: cur - step,
      PageUp: cur + 30,
      PageDown: cur - 30,
      Home: 0,
      End: duration,
    };
    const t = map[e.key];
    if (t === undefined) return;
    e.preventDefault();
    commit(t);
  };

  const playing = st ? !st.paused && !st.ended : false;
  const toggle = () => {
    if (!media || disabled) return;
    if (media.paused || media.ended) void media.play().catch(() => undefined);
    else media.pause();
  };

  return (
    <div className="controls" data-disabled={disabled || undefined}>
      <button
        type="button"
        className="ctl ctl--primary"
        onClick={toggle}
        disabled={disabled}
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? <Pause weight="fill" /> : <Play weight="fill" />}
      </button>
      <span className="controls__time mono" aria-hidden="true">
        {formatDuration(time)} <span className="controls__sep">/</span>{' '}
        {duration > 0 ? formatDuration(duration) : '--:--'}
      </span>

      <div
        ref={track}
        className="seek"
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={Math.max(0, Math.round(duration))}
        aria-valuenow={Math.round(time)}
        aria-valuetext={
          duration > 0 ? `${spoken(time)} of ${spoken(duration)}` : 'Length not known yet'
        }
        aria-disabled={disabled || duration <= 0}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={() => setDrag(null)}
        onPointerLeave={() => setHover(null)}
        onKeyDown={onKey}
      >
        <div className="seek__track">
          {duration > 0 &&
            st?.buffered.map((b, i) => (
              <span
                key={i}
                className="seek__ready"
                style={{
                  left: `${(b.start / duration) * 100}%`,
                  width: `${((b.end - b.start) / duration) * 100}%`,
                }}
              />
            ))}
          {seekableFraction < 1 && duration > 0 && (
            <span className="seek__locked" style={{ left: `${seekableFraction * 100}%` }} />
          )}
          <span className="seek__played" style={{ width: `${frac * 100}%` }} />
          <span className="seek__thumb" style={{ left: `${frac * 100}%` }} />
        </div>
        {hover !== null && duration > 0 && (
          <span className="seek__tip mono" style={{ left: `${(hover / duration) * 100}%` }}>
            {formatDuration(hover)}
          </span>
        )}
      </div>

      <button
        type="button"
        className="ctl"
        onClick={() => media && (media.muted = !media.muted)}
        aria-label={st?.muted || st?.volume === 0 ? 'Unmute' : 'Mute'}
        disabled={disabled}
      >
        {st?.muted || st?.volume === 0 ? <SpeakerSlash /> : <SpeakerHigh />}
      </button>
      <input
        className="vol"
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={st?.muted ? 0 : (st?.volume ?? 1)}
        onChange={(e) => {
          if (!media) return;
          media.muted = false;
          media.volume = Number(e.target.value);
        }}
        aria-label="Volume"
        disabled={disabled}
      />
      <button
        type="button"
        className="ctl ctl--text mono"
        onClick={() => {
          if (!media) return;
          const i = RATES.indexOf(media.playbackRate);
          media.playbackRate = RATES[(i + 1) % RATES.length] ?? 1;
        }}
        aria-label={`Playback speed ${st?.rate ?? 1} times. Activate to change.`}
        disabled={disabled}
      >
        {st?.rate ?? 1}×
      </button>
      <button
        type="button"
        className="ctl"
        onClick={onToggleFocus}
        aria-label={focus ? 'Leave focus mode' : 'Enter focus mode'}
        aria-pressed={focus}
      >
        {focus ? <ArrowsIn /> : <ArrowsOut />}
      </button>
    </div>
  );
}
